import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Drawer } from "@/components/ui/drawer";
import { Pencil, Radical, Trash } from "lucide-react";
import {
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { getApiUrl } from "@/utils/api";
import { toast } from "react-toastify";
import { DeleteModal } from "@/components/delete-modal";
import { useTranslation } from "react-i18next";
import { Pagination } from "@/components/pagination";
import { EmissionFactorForm } from "./EmissionFactorForm";
import { EmissionFactor } from "@/types";
import { PageHeader } from "@/components/PageHeader";

interface ApiResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: EmissionFactor[];
}

export default function EmissionFactorsList() {
  const { t } = useTranslation();
  const { token } = useAuth();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [itemToDelete, setItemToDelete] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const queryClient = useQueryClient();

  const PAGE_SIZE = 10;

  const { data, isLoading, error } = useQuery<ApiResponse>({
    queryKey: ["emission-factors", currentPage],
    queryFn: async () => {
      const offset = (currentPage - 1) * PAGE_SIZE;
      const response = await fetch(
        getApiUrl(`emission-factors/?limit=${PAGE_SIZE}&offset=${offset}`),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok) {
        throw new Error("Failed to fetch emission factors");
      }
      return response.json();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await fetch(getApiUrl(`emission-factors/${id}/`), {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to delete emission factor");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["emission-factors"] });
      toast.success(t("coefficients.emission.deleteSuccess"));
      setItemToDelete(null);
    },
    onError: (error: Error) => {
      toast.error(error.message || t("common.error"));
      setItemToDelete(null);
    },
  });

  const totalPages = data ? Math.ceil(data.count / PAGE_SIZE) : 1;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleEdit = (id: number) => {
    setEditingId(id);
    setIsDrawerOpen(true);
  };

  const handleAdd = () => {
    setEditingId(null);
    setIsDrawerOpen(true);
  };

  if (error) {
    return <div className="text-destructive">Error: {error.message}</div>;
  }

  return (
    <div className="flex flex-col h-full pb-8">
      <PageHeader
        icon={<Radical className="w-12 h-12 text-primary-foreground" />}
        actionTitle="coefficients.emission.add"
        title="coefficients.emission.title"
        description="coefficients.emission.description"
        handleNew={handleAdd}
      />

      <div className="flex items-center space-x-2 p-4" />

      <div className="px-4">
        <div className="rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("coefficients.emission.fields.name")}</TableHead>
                <TableHead>
                  {t("coefficients.emission.fields.formula")}
                </TableHead>
                <TableHead>
                  {t("coefficients.emission.fields.coefficient")}
                </TableHead>
                <TableHead className="text-right">
                  {t("common.actions")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading
                ? Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Skeleton className="h-4 w-[150px]" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-[200px]" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-[100px]" />
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end space-x-2">
                          <Skeleton className="h-8 w-8" />
                          <Skeleton className="h-8 w-8" />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                : data?.results.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell variant="compact" className="font-medium">
                        {item.name}
                      </TableCell>
                      <TableCell variant="compact" className="text-center">
                        {item.formula}
                      </TableCell>
                      <TableCell variant="compact" className="text-center">
                        {item.coefficient}
                      </TableCell>
                      <TableCell variant="compact" className="text-right">
                        <div className="flex justify-center items-center">
                          <div
                            role="button"
                            className="w-[30px] w-[40px] p-1 hover:bg-[#A3F800] rounded-md"
                            onClick={() => handleEdit(item.id)}
                          >
                            <Pencil
                              size={30}
                              fill="#000000"
                              stroke="#ffffff"
                              strokeWidth={1}
                            />
                          </div>
                          <div
                            role="button"
                            className="w-[30px] w-[40px] p-1 hover:bg-[#A3F800] rounded-md"
                            onClick={() => setItemToDelete(item.id)}
                          >
                            <Trash
                              size={30}
                              fill="#ff0000"
                              stroke="#ffffff"
                              strokeWidth={2}
                            />
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </div>
        {data && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            hasNextPage={!!data.next}
            hasPreviousPage={!!data.previous}
            onPageChange={handlePageChange}
          />
        )}
      </div>

      <Drawer
        open={isDrawerOpen}
        onOpenChange={(open) => {
          if (!open) {
            setIsDrawerOpen(false);
            setEditingId(null);
          }
        }}
        direction="right"
      >
        <DrawerContent className="h-full w-[500px] ml-auto">
          <DrawerHeader>
            <DrawerTitle>
              {editingId
                ? t("coefficients.emission.editTitle")
                : t("coefficients.emission.addTitle")}
            </DrawerTitle>
            <DrawerDescription>
              {editingId
                ? t("coefficients.emission.editDescription")
                : t("coefficients.emission.addDescription")}
            </DrawerDescription>
          </DrawerHeader>
          <div className="p-6">
            <EmissionFactorForm
              id={editingId || undefined}
              onSuccess={() => {
                setIsDrawerOpen(false);
                setEditingId(null);
                toast.success(
                  editingId
                    ? t("coefficients.emission.updateSuccess")
                    : t("coefficients.emission.createSuccess"),
                );
              }}
              onCancel={() => {
                setIsDrawerOpen(false);
                setEditingId(null);
              }}
            />
          </div>
        </DrawerContent>
      </Drawer>

      <DeleteModal
        open={itemToDelete !== null}
        onOpenChange={(open) => !open && setItemToDelete(null)}
        entity={t("coefficients.emission.entityName")}
        onDelete={() => deleteMutation.mutate(itemToDelete!)}
        isPending={deleteMutation.isPending}
      />
    </div>
  );
}
